import Tracker from '@openreplay/tracker'
import trackerZustand, { StateLogger } from '@openreplay/tracker-zustand'

// Klucze, których wartości nigdy nie trafiają do nagrania — nawet w payloadzie
// nieudanego zapytania.
const SENSITIVE_KEY_PATTERN =
  /pesel|pass(word)?|has[lł]o|token|secret|authorization|cardnumber|numerkarty|licen[cs]e?number|iban|accountnumber/i

// Payloady delegacji/list kierowców potrafią mieć setki kilobajtów. Ciała
// zapisujemy tylko dla 4xx/5xx, więc limit dobrany tak, by mieścił największy
// znany payload (zapis płac minimalnych, ~210 kB) — obcięty w połowie i tak
// nie nadaje się do diagnozy.
const MAX_CAPTURED_BODY_CHARS = 250000

const redactValue = (value: unknown, depth = 0): unknown => {
  if (depth > 6 || value === null || typeof value !== 'object') return value

  if (Array.isArray(value)) {
    return value.map((item) => redactValue(item, depth + 1))
  }

  const result: Record<string, unknown> = {}
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    result[key] = SENSITIVE_KEY_PATTERN.test(key)
      ? '<redacted>'
      : redactValue(val, depth + 1)
  }
  return result
}

const truncate = (text: string): string =>
  text.length > MAX_CAPTURED_BODY_CHARS
    ? `${text.slice(0, MAX_CAPTURED_BODY_CHARS)}… <truncated ${text.length - MAX_CAPTURED_BODY_CHARS} chars>`
    : text

const sanitizeBody = (body: unknown): unknown => {
  if (body == null) return body

  if (typeof body === 'string') {
    try {
      return truncate(JSON.stringify(redactValue(JSON.parse(body))))
    } catch {
      return truncate(body)
    }
  }

  if (typeof body === 'object') {
    // Blob/FormData/ArrayBuffer — nie da się bezpiecznie zserializować.
    if (
      (typeof Blob !== 'undefined' && body instanceof Blob) ||
      (typeof FormData !== 'undefined' && body instanceof FormData) ||
      (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer)
    ) {
      return null
    }
    return redactValue(body)
  }

  return body
}

const sanitizeNetworkPayload = (data: any) => {
  // Zapytanie zakończone sukcesem — zostawiamy sam wpis w liście, bez treści.
  if (
    typeof data?.status === 'number' &&
    data.status > 0 &&
    data.status < 400
  ) {
    if (data.request) data.request.body = null
    if (data.response) data.response.body = null
    return data
  }

  if (data?.request) data.request.body = sanitizeBody(data.request.body)
  if (data?.response) data.response.body = sanitizeBody(data.response.body)
  return data
}

const tracker = new Tracker({
  projectKey: process.env.OPENREPLAY_PROJECT_KEY,
  ingestPoint: process.env.OPENREPLAY_INGEST_POINT,
  respectDoNotTrack: false,
  // RODO — defensywne maskowanie danych w session replay.
  // Pokrywa: PESEL, NIP, REGON, VIN, nr rejestracyjne, kwoty, telefony,
  // e-maile (z nazwy + zawartości pól input). Bez tego nagrania zawierałyby
  // surowe dane osobowe kierowców i firm.
  defaultInputMode: 1, // wszystkie <input> obfuskowane domyślnie (***)
  obscureTextNumbers: true, // liczby w renderowanym tekście (kwoty, nr telefonów, PESEL w kartach/tabelach)
  obscureTextEmails: true, // e-maile w tekście (default true, ale jawnie)
  obscureInputNumbers: true, // liczby wpisywane w input
  obscureInputEmails: true, // e-maile wpisywane w input

  // Przechwytywanie treści zapytań sieciowych. Lista zapytań jest zbierana
  // zawsze, ale ciała (request/response body) zachowujemy WYŁĄCZNIE dla
  // odpowiedzi 4xx/5xx — inaczej w nagraniach lądowałyby pełne payloady z
  // danymi osobowymi kierowców (RODO). Bez tego błąd typu "Request failed with
  // status code 400" jest nie do zdiagnozowania, bo komunikat serwera przepada.
  network: {
    capturePayload: true,
    ignoreHeaders: ['Cookie', 'Set-Cookie', 'Authorization'],
    sanitizer: sanitizeNetworkPayload,
  },
})

// Register the zustand plugin on the tracker. The returned function wraps a
// zustand state creator so that every mutation is captured in the OpenReplay
// session replay alongside the user's interactions.
//   const useFooStore = create()(devtools(persist(
//     zustandPlugin((set) => ({ ... }), 'foo'),
//     { name: 'foo' }
//   )))
export const zustandPlugin = tracker.use(
  trackerZustand(),
) as unknown as StateLogger

export default tracker
